import {
  ActivityIndicator,
  FlatList,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  View,
} from "react-native"
import { TouchableOpacity } from "react-native-gesture-handler"
import BFText from "@/components/standard/BFText"
import { formatDate, parseDate } from "@/utils/DateUtils"
import RefreshIcon from "@/assets/icons/refresh-icon.svg"
import { useCallback, useEffect, useRef, useState } from "react"
import type { AnalysisType, UserType } from "@/generated/graphql"
import PropertyCard from "@/components/properties/PropertyCard"
import {
  useAnalyses,
  useRemoveAnalysis,
  useRunMyLeads,
  useSetFavoriteAnalysis,
  useSetAnalysisNotification,
} from "@/queries/analysis"
import { createBranchAnalysisShareLink } from "@/stores/branch"
import { useShare } from "@/hooks/useShare"
import { useUser } from "@/queries/user"
import {
  favoritesActionClicked,
  notificationActionClicked,
  propertiesListScrolled,
  propertyCardClicked,
} from "@/analytics/events/properties"
import { router, useGlobalSearchParams } from "expo-router"
import { setIsMyLeadsRunning } from "@/stores/myLeads"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import MyLeadsConnectBanner from "@/components/properties/myleads/MyLeadsConnectBanner"
import useBrowserInfo from "@/hooks/useBrowserInfo"
import LoadingBouncingBalls from "@/components/animations/LoadingBouncingBalls"
import {
  type AnalysesSortOption,
  type SortDirectionOption,
} from "@/types/sortTypes"
import { combineSortAndDirection } from "@/utils/AnalysisUtils"
import MyLeadsConnectCarouselAView from "@/components/properties/myleads/MyLeadsConnectCarouselAView"
import MyLeadsConnectCarouselBView from "@/components/properties/myleads/MyLeadsConnectCarouselBView"
import MyLeadsConnectView from "@/components/properties/myleads/MyLeadsConnectView"
import { useMyLeadsBoostFeature } from "@/hooks/useMyLeadsBoostFeature"
import { type EmptyAnalysisListType } from "@/types/analysisTypes"
import EmptyAnalysisList from "@/screens/analyze/components/EmptyAnalysisList"

const ITEM_HEIGHT = 300

interface MyLeadsRefreshProps {
  lastUpdatedAt?: string | null
  resync: () => void
}

const MyLeadsRefresh = ({ lastUpdatedAt, resync }: MyLeadsRefreshProps) => (
  <TouchableOpacity testID={"refresh-button"} onPress={resync}>
    <View className="flex flex-row items-center justify-center pt-3 pb-2">
      <BFText className="font-spacegrotesk text-lightest text-[10px]">
        {`Last Updated ${formatDate(parseDate(lastUpdatedAt), "eee, MMM d, yyyy ' at ' h:mmaa")}`}
      </BFText>
      <RefreshIcon width={26} height={25} color={"black"} />
    </View>
  </TouchableOpacity>
)

export const AnalysisList = ({
  buyBoxId,
  sort,
  direction,
  onlyFavorites = false,
  onlySourced = false,
}: {
  buyBoxId: string | null
  sort?: AnalysesSortOption
  direction?: SortDirectionOption
  onlyFavorites?: boolean
  onlySourced?: boolean
}) => {
  const scrollTo = useRef(0)
  const lastRecordedScrollTo = useRef(0)
  const {
    data: analyses,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAnalyses(
    combineSortAndDirection(sort || "updated_at", direction || "default"),
    buyBoxId,
    onlyFavorites,
    onlySourced
  )
  const removeAnalysisMutation = useRemoveAnalysis()
  const analysisNotificationMutation = useSetAnalysisNotification()
  const favoriteAnalysisMutation = useSetFavoriteAnalysis()
  const runMyLeads = useRunMyLeads()

  const [refreshing, setRefreshing] = useState(false)
  const [analysisList, setAnalysisList] = useState<any[] | null>(null)
  const share = useShare()
  const { data: user } = useUser()

  const isDesktop = useIsDesktop()
  const globalSearchParams = useGlobalSearchParams()
  const { isEmbeddedBrowser } = useBrowserInfo()

  const { variation, myLeadsEnabled } = useMyLeadsBoostFeature()

  useEffect(() => {
    if (analyses) {
      const newList: AnalysisType[] = []
      const existingIds = new Set()
      analyses.pages.forEach((page) => {
        page?.analyses?.forEach((analysis) => {
          if (!existingIds.has(analysis.id)) {
            newList.push(analysis as AnalysisType)
            existingIds.add(analysis.id)
          }
        })
      })
      setAnalysisList(newList)
    }
  }, [analyses])

  const incrementPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }

  function toggleNotifications(item: AnalysisType) {
    const notify: boolean = !item.notificationsEnabled
    item.notificationsEnabled = notify

    const newList = [...(analysisList || [])]
    notificationActionClicked(item.id, "Properties", notify)
    setAnalysisList(newList)

    analysisNotificationMutation.mutate(
      { analysisId: item.id, enableNotifications: notify },
      {
        onError: (err) => {
          console.error("Toggle Card Notification Error:", err)
        },
      }
    )
  }

  function favoriteAnalysis(item: AnalysisType) {
    const favorite = !item.isFavorite

    if (onlyFavorites) {
      const newList = [
        ...(analysisList || []).filter((analysis) => analysis.id !== item.id),
      ]
      setAnalysisList(newList)
    } else {
      item.isFavorite = favorite
      const newList = [...(analysisList || [])]
      favoritesActionClicked("Properties", favorite)
      setAnalysisList(newList)
    }
    favoriteAnalysisMutation.mutate(
      { analysisId: item.id, favorite },
      {
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  const renderProperty = useCallback(
    ({ item, index }: { item: AnalysisType; index: number }) => (
      <Pressable
        testID={`analysis-${index}`}
        className="h-[300px]"
        onPress={() => {
          onAnalysisPress(item, index)
        }}
      >
        <PropertyCard
          selected={isDesktop && globalSearchParams?.analysisId === item.id}
          analysis={item}
          onShare={shareAnalysis}
          onRemove={removeAnalysis}
          onNotify={() => {
            toggleNotifications(item)
          }}
          onFavorite={() => {
            favoriteAnalysis(item)
          }}
        />
      </Pressable>
    ),
    [analysisList, globalSearchParams]
  )

  const onAnalysisPress = (analysis: AnalysisType, index: number) => {
    propertyCardClicked({
      cardListNumber: index,
      propertyType: analysis.isSourced
        ? "sourced"
        : analysis.sharedFrom
          ? "shared"
          : "analysis",
      propertyAddress: analysis.property.fullAddress,
      roi: analysis.roiDealScore || "",
      price: analysis.purchasePrice || 0,
      arv: analysis.afterRepairValue || 0,
      profit: analysis.profitPotential || 0,
      isNew: !analysis.readAt,
    })
    router.push(`/properties/${analysis.id}`)
  }

  const onPullRefresh = () => {
    void (async () => {
      setRefreshing(true)
      await refetch()
      setRefreshing(false)
    })()
  }

  const removeAnalysis = (id: string) => {
    const newList = [
      ...(analysisList || []).filter((analysis) => analysis.id !== id),
    ]
    setAnalysisList(newList)
    removeAnalysisMutation.mutate(
      { analysisId: id },
      {
        onError: (err) => {
          console.error(err)
        },
      }
    )
  }

  const onListScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollTo.current = event.nativeEvent.contentOffset.y
  }

  const recordScrollIndex = () => {
    if (lastRecordedScrollTo.current !== scrollTo.current) {
      lastRecordedScrollTo.current = scrollTo.current
      propertiesListScrolled({
        scrolledTo: Math.ceil(scrollTo.current / ITEM_HEIGHT),
      })
    }
  }

  const resyncMyLeads = () => {
    runMyLeads.mutate(undefined, {
      onSuccess: () => {
        setIsMyLeadsRunning(true)
      },
    })
  }

  const shareAnalysis = (
    id: string,
    street: string,
    imageUrl: string | null
  ) => {
    void (async () => {
      const branchUrl = await createBranchAnalysisShareLink({
        user: user?.me as UserType,
        analysisId: id,
        street,
        imageUrl,
      })
      console.log(branchUrl)

      await share({
        message: `Check out the RE investment analysis I've created using the Backflip app for ${street}.`,
        url: branchUrl,
        title: `Backflip Analysis for ${street}`,
        subject: `Backflip Analysis for ${street}`,
      })
    })()
  }

  const ListEndLoader = () => {
    if (isLoading) {
      return <ActivityIndicator size={"large"} />
    }
  }

  if (analysisList === null) {
    return (
      <View className="flex items-center justify-center flex-1">
        <LoadingBouncingBalls />
      </View>
    )
  }

  const onDecline = () => {
    console.log("No-op TODO: If button is added to layout")
  }

  const listIsEmpty = analysisList?.length === 0
  let currentListType: EmptyAnalysisListType

  if (onlyFavorites) {
    currentListType = "favorites"
  } else if (onlySourced && buyBoxId) {
    currentListType = "myLeads-buybox"
  } else if (onlySourced && !buyBoxId) {
    currentListType = "myLeads-nobuybox"
  } else {
    currentListType = "all"
  }

  // Empty List Handling
  if (listIsEmpty && myLeadsEnabled) {
    return <EmptyAnalysisList type={currentListType} variation={variation} />
  } else if (
    listIsEmpty &&
    !myLeadsEnabled &&
    currentListType.includes("myLeads")
  ) {
    return <EmptyAnalysisList type={"carousel"} variation={variation} />
  } else if (listIsEmpty) {
    return <EmptyAnalysisList type={currentListType} variation={variation} />
  }

  return (
    <View className="flex flex-1">
      {!myLeadsEnabled && listIsEmpty ? (
        /* A/B LD Carousel options */
        variation === "treatment_1" ? (
          <MyLeadsConnectCarouselAView onDismiss={onDecline} />
        ) : variation === "treatment_2" ? (
          <MyLeadsConnectCarouselBView onDismiss={onDecline} />
        ) : (
          <MyLeadsConnectView darkTheme={true} onDismiss={onDecline} />
        )
      ) : (
        <>
          <FlatList
            data={analysisList}
            onEndReached={incrementPage}
            onEndReachedThreshold={0.8}
            renderItem={renderProperty}
            onScroll={onListScroll}
            onMomentumScrollEnd={recordScrollIndex}
            scrollEventThrottle={16}
            getItemLayout={(_, index) => ({
              length: ITEM_HEIGHT,
              offset: ITEM_HEIGHT * index,
              index,
            })}
            ListHeaderComponent={
              myLeadsEnabled ? (
                <MyLeadsRefresh
                  lastUpdatedAt={user?.me?.googleToken?.emailExtractedAt}
                  resync={resyncMyLeads}
                />
              ) : (
                <></>
              )
            }
            ListFooterComponent={ListEndLoader}
            keyExtractor={(analysis) => analysis.id}
            onRefresh={onPullRefresh}
            refreshing={refreshing}
          />
          {!myLeadsEnabled && (
            <MyLeadsConnectBanner
              onSelect={() => {
                // EN-4481
                if (isEmbeddedBrowser) {
                  // push to app install redirect
                  router.push("/properties/myleads/redirect")
                } else {
                  router.push("/properties/myleads/connect")
                }
              }}
            />
          )}
        </>
      )}
    </View>
  )
}
